var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useMemo } from "react";
import { useAppSelector, selectors } from "store";
import { useChartTimeline, percentToDisplayTime, } from "ui/components/elements/chart/chart-timeline";
import { ATM_TOLERANCE, suctionCupIdPrefix, vacuumPumpIdPrefix, xAxisKey, } from "ui/components/panels/graphs/constants";
/**
 * Looks up and interpolates the vacuum level for `pumpKey` at `apiTime`
 * from `nodeData`. Returns 0 when the array is empty.
 */
export var interpolateVacuumLevel = function (nodeData, pumpKey, apiTime) {
    var _a, _b, _c, _d;
    if (!nodeData.length)
        return 0;
    var idx = nodeData.findIndex(function (entry) { return entry[xAxisKey] >= apiTime; });
    if (idx === -1) {
        return (_a = nodeData[nodeData.length - 1][pumpKey]) !== null && _a !== void 0 ? _a : 0;
    }
    if (idx === 0) {
        return (_b = nodeData[0][pumpKey]) !== null && _b !== void 0 ? _b : 0;
    }
    var t0 = nodeData[idx - 1][xAxisKey];
    var t1 = nodeData[idx][xAxisKey];
    var v0 = (_c = nodeData[idx - 1][pumpKey]) !== null && _c !== void 0 ? _c : 0;
    var v1 = (_d = nodeData[idx][pumpKey]) !== null && _d !== void 0 ? _d : 0;
    var alpha = (apiTime - t0) / (t1 - t0);
    return v0 + alpha * (v1 - v0);
};
/**
 * Resolves the Recharts display time for the current play position, using the
 * same percent→time mapping as the graph PlayLine so any value sampled at this
 * time lines up exactly with the rendered play head. Returns undefined until
 * the timeline is available.
 */
var useCurrentApiTime = function () {
    var playLocationPercent = useAppSelector(selectors.selectPlayLocationPercent);
    var timeline = useChartTimeline();
    return useMemo(function () {
        return timeline
            ? percentToDisplayTime(playLocationPercent, timeline)
            : undefined;
    }, [timeline, playLocationPercent]);
};
/**
 * Returns the interpolated vacuum level at the current play position,
 * or undefined when graph data is not yet available.
 *
 * Uses the governing pump by default. Pass nodeId to target a specific pump node
 * (e.g. for per-grip-position animations in decentral systems).
 */
export var useCurrentVacuumLevel = function (nodeId) {
    var parsedGraphData = useAppSelector(selectors.selectParsedGraphData);
    var graphData = useAppSelector(selectors.selectGraphData);
    var apiTime = useCurrentApiTime();
    return useMemo(function () {
        if (!parsedGraphData || !graphData || apiTime === undefined)
            return undefined;
        var nodeData = parsedGraphData.nodeData;
        if (!nodeData.length)
            return undefined;
        var pumpKey = vacuumPumpIdPrefix + (nodeId !== null && nodeId !== void 0 ? nodeId : graphData.governingPump.id);
        return interpolateVacuumLevel(nodeData, pumpKey, apiTime);
    }, [parsedGraphData, graphData, apiTime, nodeId]);
};
/** The most-vacuum (minimum) level across `cupKeys` in a single node entry. */
var minCupLevel = function (entry, cupKeys) {
    return Math.min.apply(Math, __spreadArray([], __read(cupKeys.map(function (key) { var _a; return (_a = entry[key]) !== null && _a !== void 0 ? _a : 0; })), false));
};
/**
 * The first `timeData` value at which the system leaves atmospheric — the
 * earliest entry where any cup has pulled vacuum past `tolerance`. Returns
 * undefined when the cups never leave atmospheric (e.g. no cup data).
 *
 * Used as the right edge of the "atmospheric at the start" window: before this
 * time the part has not been gripped yet, so the system is still at atmospheric.
 */
export var firstVacuumTime = function (nodeData, cupKeys, tolerance) {
    if (!cupKeys.length)
        return undefined;
    var entry = nodeData.find(function (e) { return minCupLevel(e, cupKeys) < -tolerance; });
    return entry === null || entry === void 0 ? void 0 : entry[xAxisKey];
};
/**
 * Whether the system is at atmospheric pressure at `playTime`.
 *
 * ATM is a momentary state, on at two points in the cycle:
 *  - the start, before the system first pulls vacuum (`playTime < vacuumStartTime`)
 *  - the return, from the cup-release event onward (`playTime >= atmTime`)
 * and off through the vacuum-holding period in between.
 *
 * The return edge is glued to `atmTime` (`timeData[cupReleaseIndex]`, the moment
 * the cups actually return to atmospheric) so it lines up with the ATM reference
 * line drawn on the charts. When that index is unavailable (`atmTime` undefined)
 * it falls back to the momentary slowest-cup level so the indicator still returns
 * on after release.
 */
export var isAtAtmosphere = function (playTime, vacuumStartTime, atmTime, slowestCupNow, tolerance) {
    // Never leaves atmospheric (no vacuum at all) → always on.
    if (vacuumStartTime === undefined)
        return true;
    // Start window: still atmospheric before the first vacuum build-up.
    if (playTime < vacuumStartTime)
        return true;
    // Vacuum-holding period until the part is released back to atmospheric.
    if (atmTime !== undefined)
        return playTime >= atmTime;
    return slowestCupNow >= -tolerance;
};
/**
 * Hook form of {@link isAtAtmosphere}, resolved at the current play position.
 *
 * Drives the ATM indicator. Tracks the suction cups (not the governing pump):
 * the pump reaches blow-off overpressure before the cups release, which lit the
 * indicator too early when it tracked the pump.
 */
export var useIsAtAtmosphere = function () {
    var parsedGraphData = useAppSelector(selectors.selectParsedGraphData);
    var graphData = useAppSelector(selectors.selectGraphData);
    var apiTime = useCurrentApiTime();
    return useMemo(function () {
        if (!parsedGraphData || !graphData || apiTime === undefined)
            return false;
        var nodeData = parsedGraphData.nodeData;
        var _a = graphData.systemProperty, timeData = _a.timeData, cupReleaseIndex = _a.cupReleaseIndex;
        if (!nodeData.length || !graphData.suctionCups.length)
            return false;
        var cupKeys = graphData.suctionCups.map(function (cup) { return suctionCupIdPrefix + cup.id; });
        var vacuumStartTime = firstVacuumTime(nodeData, cupKeys, ATM_TOLERANCE);
        var atmTime = cupReleaseIndex >= 0 ? timeData[cupReleaseIndex] : undefined;
        var slowestCupNow = Math.min.apply(Math, __spreadArray([], __read(cupKeys.map(function (key) {
            return interpolateVacuumLevel(nodeData, key, apiTime);
        })), false));
        return isAtAtmosphere(apiTime, vacuumStartTime, atmTime, slowestCupNow, ATM_TOLERANCE);
    }, [parsedGraphData, graphData, apiTime]);
};
